import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createVNode as _createVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, unref as _unref, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "ml-8" }
const _hoisted_2 = { key: 0 }

import { PropType, computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { NEW_ID } from '@/util/constants';
import { useEvents, Event } from '@/services/events.composable';
import { Actions, Mutations, ShiftCMSState } from '@/store';
import { useStore } from 'vuex';
import { ContentStoreState, ContentStoreActions } from '@/stores/content.store';
import { DataResponse } from '@/services/base.service';
import { Discount, DiscountCode } from '@/models/discounts.model';
import { Initiative } from '@/models/initiative.model';
import CurrentLanguage from '@/components/CurrentLanguage.vue';
import DateField from '@/components/common/DateField.vue';
import { addHours, format, parse } from 'date-fns';

interface CodeGroup {
  usedCodes: number;
  codes: number;
  validity: string | null;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'DiscountForm',
  props: {
  data: {
    type: Object as PropType<Discount>,
    required: true,
  },
},
  setup(__props) {

const events = useEvents();
const store = useStore<ShiftCMSState & { cns: ContentStoreState }>();

const initiatives = computed<Initiative[]>(() => store.state.cns.initiatives);

const props = __props;

const model = ref<Discount>({
  ...props.data,
});

const codes = ref<number>(0);
const validity = ref<string>('');

const action = computed<string>(() =>
  model.value.id === NEW_ID ? 'Add' : 'Save',
);

const codeGroups = computed<CodeGroup[]>(() =>
  props.data.codes.reduce((acc: CodeGroup[], item: DiscountCode) => {
    let group = acc.find((d) => d.validity === item.validityDate) || {
      validity: item.validityDate,
      codes: 0,
      usedCodes: 0,
    };

    group.validity = item.validityDate;
    group.codes = props.data.codes.filter(
      (v) => v.validityDate === group.validity,
    ).length;
    group.usedCodes = props.data.codes.filter(
      (v) => v.used && v.validityDate === group.validity,
    ).length;

    return [...acc.filter((g) => g.validity !== item.validityDate), group];
  }, []),
);

const valid = ref<boolean>(true);

const cancel = () => {
  store.commit(Mutations.HideDrawer);
};

const saveItem = async () => {
  let res: DataResponse<Discount | null> = {
    status: false,
    data: null,
  };

  if (model.value.id === NEW_ID) {
    res = await store.dispatch(ContentStoreActions.CreateDiscount, model.value);
  } else {
    res = await store.dispatch(ContentStoreActions.UpdateDiscount, model.value);
  }

  if (res.status) {
    store.dispatch(Actions.ShowNotification, {
      message: `Saved Schedule`,
      type: 'success',
    });
    events.emit(Event.UpdateContentItem);
    cancel();
  } else {
    store.dispatch(Actions.ShowNotification, {
      message: `Failed to saved Schedule`,
      type: 'error',
    });
  }
};

const removeItem = async () => {
  if (
    model.value &&
    model.value.id !== NEW_ID &&
    confirm(`Are you absolutely sure you want to delete this discount?`)
  ) {
    store.dispatch(ContentStoreActions.DeleteDiscount, model.value.id);

    store.commit(Mutations.HideDrawer);
  }
};

const updateLanguage = async () => {
  if (model.value.id === NEW_ID) {
    Object.assign(model, { title: '' });
  } else {
    const dis = await store.dispatch(
      ContentStoreActions.GetDiscount,
      model.value.id,
    );

    if (dis) {
      Object.assign(model.value, { ...dis });
    }
  }
};

onMounted(() => {
  events.on(Event.LanguageChanged, updateLanguage);
  if (props.data) {
    Object.assign(model.value, props.data);
  }
  store.dispatch(ContentStoreActions.GetInitiatives);
});

onBeforeUnmount(() => {
  events.off(Event.LanguageChanged, updateLanguage);
});

function parseCSV() {
  if (model.value.codesFile) {
    var reader = new FileReader();
    reader.readAsText(model.value.codesFile, 'UTF-8');

    reader.onload = function (evt: ProgressEvent<FileReader>) {
      const text = evt.target?.result;
      if (text && typeof text === 'string') {
        const lines = text.split('\n').filter((v) => !!v);
        lines.shift(); // remove header

        model.value.codes = [
          ...model.value.codes,
          ...lines.map((l) => {
            return {
              id: -1,
              code: l.split(',')[0],
              validityDate: validity.value
                ? addHours(
                    parse(validity.value, 'dd-MM-yyyy', new Date()),
                    12,
                  ).toISOString()
                : null,
              used: false,
            };
          }),
        ];
        codes.value = lines.length;
      }
    };
  }
}

function updateCodes() {
  // find new codes
  const codes = model.value.codes.filter((v) => v.id === -1);

  model.value.codes = [
    ...model.value.codes.filter((v) => v.id !== -1),
    ...codes.map((l) => {
      return {
        ...l,
        validityDate: validity.value
          ? addHours(
              parse(validity.value, 'dd-MM-yyyy', new Date()),
              12,
            ).toISOString()
          : null,
      };
    }),
  ];
}

const showCodeInfo = ref<boolean>(false);
function showInfo() {
  showCodeInfo.value = !showCodeInfo.value;
}

return (_ctx: any,_cache: any) => {
  const _component_v_alert = _resolveComponent("v-alert")!
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_autocomplete = _resolveComponent("v-autocomplete")!
  const _component_v_file_input = _resolveComponent("v-file-input")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_container = _resolveComponent("v-container")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_form = _resolveComponent("v-form")!

  return (_openBlock(), _createBlock(_component_v_form, {
    modelValue: valid.value,
    "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((valid).value = $event)),
    onSubmit: _withModifiers(saveItem, ["prevent"])
  }, {
    default: _withCtx(() => [
      _createVNode(_component_v_container, null, {
        default: _withCtx(() => [
          _createElementVNode("h2", null, [
            _createTextVNode(_toDisplayString(_ctx.$t(action.value)) + " Discount ", 1),
            _createVNode(CurrentLanguage)
          ]),
          _createVNode(_component_v_alert, { type: "info" }, {
            default: _withCtx(() => _cache[7] || (_cache[7] = [
              _createTextVNode(" A discount can be added to an initiative. ")
            ])),
            _: 1
          }),
          _createVNode(_component_v_row, { class: "mt-8" }, {
            default: _withCtx(() => [
              _createVNode(_component_v_col, null, {
                default: _withCtx(() => [
                  _createVNode(_component_v_text_field, {
                    modelValue: model.value.title,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((model.value.title) = $event)),
                    label: "Title",
                    "hide-details": "",
                    required: ""
                  }, null, 8, ["modelValue"])
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          _createVNode(_component_v_row, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_col, null, {
                default: _withCtx(() => [
                  _createVNode(_component_v_text_field, {
                    modelValue: model.value.description,
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((model.value.description) = $event)),
                    label: "Description",
                    "hide-details": "",
                    required: ""
                  }, null, 8, ["modelValue"])
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          _createVNode(_component_v_row, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_col, null, {
                default: _withCtx(() => [
                  _createVNode(_component_v_text_field, {
                    modelValue: model.value.emailText,
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((model.value.emailText) = $event)),
                    label: "Email text",
                    "hide-details": "",
                    required: ""
                  }, null, 8, ["modelValue"])
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          _createVNode(_component_v_row, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_col, null, {
                default: _withCtx(() => [
                  _createVNode(_component_v_autocomplete, {
                    modelValue: model.value.initiative,
                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((model.value.initiative) = $event)),
                    items: initiatives.value,
                    label: "Initiative",
                    "item-text": "title",
                    "return-object": "",
                    required: ""
                  }, null, 8, ["modelValue", "items"])
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          _createVNode(_component_v_row, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_col, { cols: "6" }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_file_input, {
                    modelValue: model.value.codesFile,
                    "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((model.value.codesFile) = $event)),
                    label: "Codes CSV",
                    onChange: parseCSV,
                    hint: codes.value ? `${codes.value} codes found` : '',
                    "append-icon": "mdi-help-circle-outline",
                    "onClick:append": showInfo
                  }, null, 8, ["modelValue", "hint"])
                ]),
                _: 1
              }),
              _createVNode(_component_v_col, null, {
                default: _withCtx(() => [
                  _createVNode(DateField, {
                    modelValue: validity.value,
                    "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((validity).value = $event)),
                    label: "Validity Date",
                    onChange: updateCodes
                  }, null, 8, ["modelValue"])
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          _createVNode(_component_v_row, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_col, { cols: "2" }),
              _createVNode(_component_v_col, { cols: "6" }, {
                default: _withCtx(() => [
                  (showCodeInfo.value)
                    ? (_openBlock(), _createBlock(_component_v_alert, {
                        key: 0,
                        type: "warning"
                      }, {
                        default: _withCtx(() => _cache[8] || (_cache[8] = [
                          _createTextVNode(" Upload a CSV with a single column that contains the code. Ensure the column has a header row. It should looks something like this: "),
                          _createElementVNode("pre", { style: {"margin":"16px 24px 0 24px"} }, "                Code\n                abc1\n                abc2\n                abc3\n                ...\n              ", -1)
                        ])),
                        _: 1
                      }))
                    : _createCommentVNode("", true)
                ]),
                _: 1
              }),
              _createVNode(_component_v_col, { cols: "2" })
            ]),
            _: 1
          }),
          (codeGroups.value.length)
            ? (_openBlock(), _createBlock(_component_v_card, {
                key: 0,
                elevation: "2",
                variant: "tonal",
                class: "ml-10 mr-10"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_card_text, null, {
                    default: _withCtx(() => [
                      _cache[9] || (_cache[9] = _createElementVNode("h3", null, "Codes", -1)),
                      _createElementVNode("ul", _hoisted_1, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(codeGroups.value, (group) => {
                          return (_openBlock(), _createElementBlock("li", {
                            key: `${group.validity}`
                          }, [
                            _createTextVNode(_toDisplayString(group.usedCodes) + " used /" + _toDisplayString(group.codes) + " total", 1),
                            (group.validity)
                              ? (_openBlock(), _createElementBlock("i", _hoisted_2, ", valid until " + _toDisplayString(_unref(format)(new Date(group.validity), 'do MMMM yyyy')), 1))
                              : _createCommentVNode("", true)
                          ]))
                        }), 128))
                      ])
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }),
      _createVNode(_component_v_row, null, {
        default: _withCtx(() => [
          _createVNode(_component_v_col, { col: "2" }),
          _createVNode(_component_v_col, null, {
            default: _withCtx(() => [
              (model.value.id !== -1)
                ? (_openBlock(), _createBlock(_component_v_btn, {
                    key: 0,
                    class: "mt-2",
                    type: "button",
                    color: "warning",
                    onClick: removeItem,
                    block: ""
                  }, {
                    default: _withCtx(() => _cache[10] || (_cache[10] = [
                      _createTextVNode("Delete")
                    ])),
                    _: 1
                  }))
                : _createCommentVNode("", true)
            ]),
            _: 1
          }),
          _createVNode(_component_v_col, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_btn, {
                class: "mt-2",
                type: "button",
                onClick: cancel,
                block: ""
              }, {
                default: _withCtx(() => _cache[11] || (_cache[11] = [
                  _createTextVNode("Cancel")
                ])),
                _: 1
              })
            ]),
            _: 1
          }),
          _createVNode(_component_v_col, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_btn, {
                class: "mt-2",
                type: "submit",
                color: "success",
                block: ""
              }, {
                default: _withCtx(() => _cache[12] || (_cache[12] = [
                  _createTextVNode("Edit")
                ])),
                _: 1
              })
            ]),
            _: 1
          }),
          _createVNode(_component_v_col, { col: "2" })
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["modelValue"]))
}
}

})